import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, fmtDateLong, fmtTime, payLabel, money } from "@/lib/api";
import { StatusPill, MatchBadge, UrgentPill, ReliabilityBadge, RepeatPill } from "@/components/StatusPill";
import { MatchReasons } from "@/components/ShiftCard";
import { MapPin, ArrowLeft, ShieldCheck, Star, RotateCcw } from "lucide-react";
import ReportDialog from "@/components/ReportDialog";

const RatingDialog = ({ open, onClose, onSubmit, workerName, busy }) => {
  const [stars, setStars] = useState(5);
  const [noShow, setNoShow] = useState(false);
  const [comment, setComment] = useState("");
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 sm:items-center" data-testid="rating-dialog">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
        <h3 className="font-display text-xl font-semibold">Review {workerName}</h3>
        <p className="mt-1 text-sm text-slate-600">Ratings build the worker's reliability score.</p>
        <div className="mt-5 flex gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => {
                setStars(n);
                setNoShow(false);
              }}
              data-testid={`rating-star-${n}`}
              className="p-1"
            >
              <Star className={`h-7 w-7 ${!noShow && n <= stars ? "fill-accentx text-accentx" : "text-slate-300"}`} />
            </button>
          ))}
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={noShow} onChange={(e) => setNoShow(e.target.checked)} data-testid="rating-no-show" />
          Worker did not show up
        </label>
        <textarea
          className="field-input mt-4 min-h-[80px]"
          placeholder="Optional note for your own records"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          data-testid="rating-comment"
        />
        <div className="mt-5 flex gap-3">
          <button
            className="btn-primary"
            disabled={busy}
            onClick={() => onSubmit({ stars: noShow ? null : stars, no_show: noShow, comment })}
            data-testid="rating-submit"
          >
            {busy ? "Saving…" : "Complete & submit"}
          </button>
          <button className="btn-ghost" onClick={onClose} data-testid="rating-cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default function PharmacyShiftMatches() {
  const { shiftId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [rating, setRating] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get(`/pharmacy/shifts/${shiftId}/matches`);
    setData(res.data);
  }, [shiftId]);

  useEffect(() => {
    load();
  }, [load]);

  const request = async (workerId) => {
    setBusy(workerId);
    setError("");
    try {
      await api.post(`/pharmacy/shifts/${shiftId}/request`, { worker_id: workerId });
      await load();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const changeStatus = async (status) => {
    setError("");
    try {
      await api.post(`/pharmacy/shifts/${shiftId}/status`, { status });
      await load();
    } catch (e) {
      setError(apiError(e));
    }
  };

  if (!data) return <p className="text-sm text-slate-500">Loading matches…</p>;
  const { shift, matches } = data;
  const past = shift.date < new Date().toISOString().slice(0, 10);
  const repeatWorkers = matches.filter((w) => w.times_worked_here > 0);

  const submitRating = async (payload) => {
    setBusy("rating");
    setError("");
    try {
      await api.post(`/pharmacy/shifts/${shiftId}/rating`, payload);
      setRating(false);
      await load();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="pharmacy-shift-matches">
      <Link to="/pharmacy" className="inline-flex items-center gap-2 text-sm text-slate-500">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>

      <div className="card-surface mt-5 flex flex-wrap items-start justify-between gap-5 p-6">
        <div>
          <p className="overline">{shift.coverage_type} coverage</p>
          <h1 className="mt-2 font-display text-2xl font-semibold tracking-tight sm:text-3xl">
            Matching {shift.worker_role}s
          </h1>
          <p className="mt-2 flex items-center gap-2 text-sm text-slate-600">
            <MapPin className="h-4 w-4" /> {shift.location_label} · {fmtDateLong(shift.date)} ·{" "}
            {fmtTime(shift.start_time)}–{fmtTime(shift.end_time)} · {payLabel(shift)}
          </p>
          <p className="mt-3 max-w-xl text-xs leading-relaxed text-slate-500" data-testid="matching-verify-notice">
            Verification status reflects only checks that have actually happened. You remain responsible for
            independently verifying identity, licence, registration, certification and eligibility to work
            before confirming a worker. FindRxStaff does not set, collect or guarantee pay.
          </p>
        </div>
        <div className="flex flex-col items-end gap-3">
          {shift.urgent && <UrgentPill />}
          <StatusPill status={shift.status} />
          <div className="flex flex-wrap gap-2">
            {shift.status !== "Cancelled" && shift.status !== "Completed" && (
              <button onClick={() => changeStatus("Cancelled")} className="btn-ghost" data-testid="cancel-shift">
                Cancel shift
              </button>
            )}
            {shift.status === "Confirmed" && (
              <button onClick={() => setRating(true)} className="btn-primary" data-testid="complete-shift">
                Mark completed & rate
              </button>
            )}
          </div>
        </div>
      </div>

      {shift.confirmed_worker && (
        <div className="card-surface mt-6 p-6" data-testid="confirmed-panel">
          <p className="overline">Confirmed</p>
          <h2 className="mt-2 font-display text-xl font-semibold">{shift.confirmed_worker.full_name}</h2>
          <p className="mt-1 text-sm text-slate-600">
            {shift.confirmed_worker.phone} · {shift.confirmed_worker.email}
          </p>
        </div>
      )}

      {error && <p className="mt-6 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="match-error">{error}</p>}

      {repeatWorkers.length > 0 && !shift.confirmed_worker && (
        <div className="mt-8" data-testid="rebook-section">
          <div className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-brand" />
            <h2 className="font-display text-xl font-semibold">Re-book someone who worked here before</h2>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {repeatWorkers.map((w) => (
              <div key={`rb-${w.worker_id}`} className="card-surface flex items-center justify-between gap-3 p-4" data-testid={`rebook-${w.worker_id}`}>
                <div>
                  <p className="font-medium">{w.full_name || w.display_name}</p>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    <RepeatPill count={w.times_worked_here} testId={`repeat-${w.worker_id}`} />
                    <ReliabilityBadge reliability={w.reliability} testId={`reliability-${w.worker_id}`} />
                  </div>
                </div>
                {!w.request_status && (
                  <button
                    onClick={() => request(w.worker_id)}
                    disabled={busy === w.worker_id || ["Confirmed", "Completed", "Cancelled"].includes(shift.status)}
                    className="btn-primary shrink-0"
                    data-testid={`rebook-request-${w.worker_id}`}
                  >
                    Re-book
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="mt-10 font-display text-xl font-semibold">
        {matches.length} qualified {shift.worker_role.toLowerCase()}
        {matches.length === 1 ? "" : "s"}
      </h2>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        {matches.map((w) => (
          <div key={w.worker_id} className="card-surface p-6" data-testid={`match-card-${w.worker_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-display text-lg font-semibold">{w.full_name || w.display_name}</h3>
                <p className="mt-1 text-sm text-slate-500">
                  {w.distance != null ? `${w.distance} miles away` : "Distance unavailable"} ·{" "}
                  {w.years_experience ?? "—"} yrs exp
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  {w.availability?.availability_status === "available"
                    ? `Available ${fmtTime(w.availability.start_time)} – ${fmtTime(w.availability.end_time)}`
                    : w.availability?.availability_status === "flexible"
                    ? "Flexible on this date"
                    : "No availability set"}
                </p>
                <p className="mt-1 text-sm text-slate-500">Minimum rate: {money(w.minimum_rate)}/hr</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <MatchBadge score={w.match_score} testId={`match-score-${w.worker_id}`} />
                <ReliabilityBadge reliability={w.reliability} testId={`match-reliability-${w.worker_id}`} />
                <RepeatPill count={w.times_worked_here} testId={`match-repeat-${w.worker_id}`} />
                <span className="pill border-slate-200 bg-slate-50 text-slate-600">
                  <ShieldCheck className="h-3.5 w-3.5" /> {w.verification_status}
                </span>
                {w.expressed_interest && (
                  <span className="pill border-purple-200 bg-purple-50 text-purple-800" data-testid={`interested-${w.worker_id}`}>
                    I'm available
                  </span>
                )}
              </div>
            </div>

            <div className="mt-4">
              <MatchReasons reasons={w.match_reasons.filter((r) => r.ok)} />
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              {w.request_status === "pending" ? (
                <span className="pill border-blue-200 bg-blue-50 text-brand" data-testid={`requested-${w.worker_id}`}>
                  Requested — waiting on worker
                </span>
              ) : w.request_status === "accepted" ? (
                <span className="pill border-emerald-200 bg-emerald-50 text-emerald-700">Accepted</span>
              ) : w.request_status === "declined" ? (
                <span className="pill border-red-200 bg-red-50 text-red-700">Declined</span>
              ) : (
                <button
                  onClick={() => request(w.worker_id)}
                  disabled={busy === w.worker_id || ["Confirmed", "Completed", "Cancelled"].includes(shift.status)}
                  className="btn-primary"
                  data-testid={`request-${w.worker_id}`}
                >
                  {busy === w.worker_id ? "Requesting…" : "Request"}
                </button>
              )}
              <div className="ml-auto">
                <ReportDialog
                  targetType="worker"
                  targetId={w.worker_id}
                  blockUserId={w.worker_id}
                  label="this worker"
                  testIdPrefix={`report-worker-${w.worker_id}`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <RatingDialog
        open={rating}
        onClose={() => setRating(false)}
        onSubmit={submitRating}
        workerName={shift.confirmed_worker?.full_name || "this worker"}
        busy={busy === "rating"}
      />

    </div>
  );
}
